import { type ReactNode, type Ref, useState } from "react";
import { useTranslations } from "@acto/web-common/locales";
import { ListTitle } from "@acto/taskpane/field-sales-2/components/primitives/ProductTrendList";
import { type ProductGroups, useProductGroups } from "@acto/taskpane/field-sales-2/data-access/useProductGroups";
import { analysisRowsKind } from "@acto/taskpane/field-sales-2/utils/chatScope";
import { ProductAnalysisView } from "./ProductAnalysisView";
import type { ProductDrillLevel } from "./ProductDrillContext";
import { type AnalysisListControls, useAnalysisListControls } from "./useAnalysisListControls";

export interface OrdersProductList {
  groups: ProductGroups;
  controls: AnalysisListControls;
  title: string;
  emptyLabel: string;
}

/** The customer's product groups under `parent` (the top level without one), valued and ranked by order intake. */
export function useOrdersProductList({
  clientId,
  parent,
  dateRange,
  enabled,
}: {
  clientId: string | undefined;
  parent: ProductDrillLevel | undefined;
  dateRange: { startDate: string; endDate: string } | null | undefined;
  enabled: boolean;
}): OrdersProductList {
  const t = useTranslations("fieldSalesTwo.analysis.orders");
  const tAnalysis = useTranslations("fieldSalesTwo.analysis");
  const controls = useAnalysisListControls(parent?.id);
  const groups = useProductGroups({
    clientId,
    enabled: enabled && !!clientId,
    parents: parent ? [parent.id] : undefined,
    dateRange,
    searchTerm: controls.debouncedSearch,
    trends: controls.trends,
    byOrderIntake: true,
  });

  // A search or trend filter that matches nothing leaves no row to read the kind from, so the
  // level keeps the kind its rows last showed instead of falling back to "subgroups".
  const visibleKind = analysisRowsKind(groups.items);
  const [lastKind, setLastKind] = useState<{ parentId: string; kind: NonNullable<typeof visibleKind> }>();
  if (parent && visibleKind && (lastKind?.parentId !== parent.id || lastKind.kind !== visibleKind)) {
    setLastKind({ parentId: parent.id, kind: visibleKind });
  }
  const rowsKind = visibleKind ?? (lastKind && lastKind.parentId === parent?.id ? lastKind.kind : undefined);

  const title = !parent ? tAnalysis("productGroups") : rowsKind === "products" ? tAnalysis("products") : t("subgroups");

  const emptyLabel = !parent
    ? t("productListEmpty")
    : rowsKind === "products"
      ? t("productsEmpty")
      : rowsKind === "product_groups"
        ? t("subgroupsEmpty")
        : t("entriesEmpty");

  return { groups, controls, title, emptyLabel };
}

interface OrdersProductPathProps {
  card: ReactNode;
  list: OrdersProductList;
  rootListTitle: ReactNode;
  isTopLevel: boolean;
  headerSentinelRef?: Ref<HTMLDivElement>;
  listHeaderRef?: Ref<HTMLDivElement>;
  searchExpanded: boolean;
  onSearchExpandedChange: (expanded: boolean) => void;
}

export function OrdersProductPath({
  card,
  list,
  rootListTitle,
  isTopLevel,
  headerSentinelRef,
  listHeaderRef,
  searchExpanded,
  onSearchExpandedChange,
}: OrdersProductPathProps) {
  return (
    <ProductAnalysisView
      kind="custom"
      card={card}
      groups={list.groups}
      searchValue={list.controls.searchValue}
      onSearchChange={list.controls.onSearchChange}
      titleSlot={isTopLevel ? rootListTitle : <ListTitle>{list.title}</ListTitle>}
      headerSentinelRef={headerSentinelRef}
      listHeaderRef={listHeaderRef}
      searchExpanded={searchExpanded}
      onSearchExpandedChange={onSearchExpandedChange}
      filterSlot={list.controls.filterSlot}
      opensProducts
      emptyLabel={list.emptyLabel}
    />
  );
}
